import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu, MessageCircle, Star, X, Gift, Heart, Sparkles, Sun, Moon,
  ChevronRight, CheckCircle2, Maximize2, Search
} from 'lucide-react';
import BookingView from './BookingView';
import logo from '../../images/logo.jpg';

import img1 from '../../images/20251125_174158.jpg.jpeg';
import img2 from '../../images/20251205_221210.jpg.jpeg';
import img3 from '../../images/20260101_200124.jpg.jpeg';
import img4 from '../../images/20260104_203059.jpg.jpeg';
import img5 from '../../images/20260104_203158.jpg.jpeg';
import img6 from '../../images/IMG-20250903-WA0010.jpg.jpeg';
import img7 from '../../images/IMG-20250910-WA0022.jpg.jpeg';
import img8 from '../../images/IMG-20250910-WA0029.jpg.jpeg';
import img9 from '../../images/IMG-20251119-WA0066.jpg.jpeg';


const BestSurpriseApp = () => {
  // --- Navigation Hook ---
  const navigate = useNavigate();

  // --- States ---
  const [view, setView] = useState('landing');
  const [lang, setLang] = useState('en');
  const [darkMode, setDarkMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [popHearts, setPopHearts] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedImg, setSelectedImg] = useState(null);

  // Booking Logic States
  const [bookingStep, setBookingStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState("");
  const [userData, setUserData] = useState({ name: "", phone: "" });

  // --- Assets ---
  const googleIconUrl = "https://www.gstatic.com/images/branding/product/2x/googleg_48dp.png";
  const heroImages = [
    "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&q=80&w=1920",
    "https://images.unsplash.com/photo-1513151233558-d860c5398176?auto=format&fit=crop&q=80&w=1920",
    "https://images.unsplash.com/photo-1507504031003-b417219a0fde?auto=format&fit=crop&q=80&w=1920"
  ];


  const galleryImages = [
    { url: img1, size: "col-span-2 row-span-2", title: "Grand Decor", tag: "Premium" },
    { url: img2, size: "col-span-1 row-span-1", title: "Surprise", tag: "Events" },
    { url: img3, size: "col-span-1 row-span-2", title: "Proposal", tag: "Love" },
    { url: img4, size: "col-span-1 row-span-1", title: "Birthday", tag: "Party" },
    { url: img5, size: "col-span-2 row-span-1", title: "Anniversary", tag: "Romance" },
    { url: img6, size: "col-span-1 row-span-1", title: "Decor", tag: "Style" },
    { url: img7, size: "col-span-1 row-span-1", title: "Celebration", tag: "Moments" },
    { url: img8, size: "col-span-1 row-span-1", title: "Surprise", tag: "Happiness" },
    { url: img9, size: "col-span-1 row-span-1", title: "Night Event", tag: "Magic" }
  ];

  const allReviews = [
    {
      name: "Kannan Raju",
      text: "It was a wonderful experience celebrating our 30th anniversary. The surprise setup truly exceeded expectations. Professional, supportive, and very affordable!",
      time: "1 week ago",
      avatar: "https://i.pravatar.cc/150?u=kannan",
      role: "Verified Customer"
    },
    {
      name: "Dhaarani",
      text: "An urgent surprise but it turned out wonderful! Polite communication and they turned it around so quick. We all loved your work!",
      time: "2 months ago",
      avatar: "https://i.pravatar.cc/150?u=dhaarani",
      role: "Verified Customer"
    },
    {
      name: "Catherinre Rose",
      text: "Amazing service at a cheap and best price. Unlike other places, they didn't charge extra for additional family members. Highly recommended!",
      time: "3 months ago",
      avatar: "https://i.pravatar.cc/150?u=catherine",
      role: "Verified Customer"
    },
    {
      name: "Sameera Banu",
      text: "I really liked that place. It felt safe and was definitely worth the 1999/- price. I’m sure I’ll go there again next time.",
      time: "1 week ago",
      avatar: "https://i.pravatar.cc/150?u=sameera",
      role: "Verified Customer"
    },
    {
      name: "Swami Nathan",
      text: "Such a beautiful celebration in my life with my life partner. Thank you so much for the best surprise, keep going!",
      time: "1 week ago",
      avatar: "https://i.pravatar.cc/150?u=swami",
      role: "Verified Customer"
    },
    {
      name: "Pooja G",
      text: "This was an amazing birthday celebration. Their welcome is so good and kind. Truly memorable moments and amazing work.",
      time: "3 months ago",
      avatar: "https://i.pravatar.cc/150?u=pooja",
      role: "Verified Customer"
    },
  ];

  // --- Multi-Language Content ---
  const content = {
    en: {
      nav: ["Services", "Gallery", "Reviews", "Contact", "Track Booking"],
      heroTag: "#1 Surprise Planner in Chennai",
      heroTitle: <>Book the <span className="text-amber-400">Perfect Moment</span></>,
      heroSub: "Chennai's premium platform for birthdays, anniversaries, and romantic proposals.",
      ctaBook: "Book Now",
      ctaChat: "Chat with Planner",
      services: [
        { title: "Birthday Bash", desc: "Private theatre, neon themes, and premium balloons.", icon: <Gift className="text-amber-500" />, img: "https://images.unsplash.com/photo-1530103043960-ef38714abb15?auto=format&fit=crop&q=80&w=800" },
        { title: "Anniversary Party", desc: "Romantic candlelight dinners and floral pathways.", icon: <Heart className="text-red-500" />, img: "https://images.unsplash.com/photo-1507504031003-b417219a0fde?auto=format&fit=crop&q=80&w=800" },
        { title: "Bride to Be", desc: "Elegant floral setups for her special countdown.", icon: <Sparkles className="text-pink-500" />, img: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80&w=800" },
        { title: "Groom to Be", desc: "Sophisticated setups for the man of the hour.", icon: <Star className="text-blue-500" />, img: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&q=80&w=1200" },
        { title: "Love Proposal", desc: "Magical beach or rooftop 'I Love You' setups.", icon: <MessageCircle className="text-rose-500" />, img: "https://images.unsplash.com/photo-1518107616985-bd48230d3b20?auto=format&fit=crop&q=80&w=800" },
        { title: "Marriage Proposal", desc: "The big 'Marry Me' neon signs and violinists.", icon: <Star className="text-amber-500" />, img: "https://images.unsplash.com/photo-1529634806980-85c3dd6d34ac?auto=format&fit=crop&q=80&w=800" },
        { title: "Normal Party", desc: "Casual gatherings with professional decor.", icon: <Moon className="text-amber-500" />, img: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&q=80&w=800" }
      ]
    },
    ta: {
      nav: ["சேவைகள்", "கேலரி", "மதிப்புரைகள்", "தொடர்பு", "முன்பதிவு நிலை"],
      heroTag: "#1 சென்னையின் சிறந்த சர்ப்ரைஸ் பிளானர்",
      heroTitle: <>சிறந்த <span className="text-amber-400">தருணத்தை</span> முன்பதிவு செய்யுங்கள்</>,
      heroSub: "சென்னையின் பிறந்தநாள் மற்றும் திருமண நாள் நிகழ்வுகளுக்கான முதன்மை தளம்.",
      ctaBook: "முன்பதிவு செய்க",
      ctaChat: "திட்டமிடுபவருடன் பேச",
      services: [
        { title: "பிறந்தநாள்", desc: "பிரைவேட் தியேட்டர் மற்றும் அலங்காரங்கள்.", icon: <Gift className="text-amber-500" />, img: "https://images.unsplash.com/photo-1530103043960-ef38714abb15?auto=format&fit=crop&q=80&w=800" },
        { title: "திருமண நாள்", desc: "மெழுகுவர்த்தி வெளிச்சத்தில் காதல் டின்னர்.", icon: <Heart className="text-red-500" />, img: "https://images.unsplash.com/photo-1507504031003-b417219a0fde?auto=format&fit=crop&q=80&w=800" },
        { title: "பிரைடு டு பி", desc: "மணப்பெண்ணுக்கான பிரத்யேக அலங்காரங்கள்.", icon: <Sparkles className="text-pink-500" />, img: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80&w=800" },
        { title: "குரூம் டு பி", desc: "மணமகனுக்கான ஸ்டைலிஷ் அலங்காரங்கள்.", icon: <Star className="text-blue-500" />, img: "https://images.unsplash.com/photo-1505232458567-cc265c538dd9?auto=format&fit=crop&q=80&w=800" },
        { title: "காதல் முன்மொழிவு", desc: "காதலை வெளிப்படுத்த அழகான அமைப்புகள்.", icon: <MessageCircle className="text-rose-500" />, img: "https://images.unsplash.com/photo-1518107616985-bd48230d3b20?auto=format&fit=crop&q=80&w=800" },
        { title: "திருமண முன்மொழிவு", desc: "திருமணத்திற்கான பிரம்மாண்டமான ஏற்பாடுகள்.", icon: <Star className="text-amber-500" />, img: "https://images.unsplash.com/photo-1529634806980-85c3dd6d34ac?auto=format&fit=crop&q=80&w=800" },
        { title: "சாதாரண பார்ட்டி", desc: "அனைத்து விதமான கொண்டாட்டங்கள்.", icon: <Moon className="text-amber-500" />, img: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&q=80&w=800" }
      ]
    }
  };
  const t = content[lang];

  // --- Effects ---
  useEffect(() => {
    if (view === 'landing') {
      const timer = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % heroImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [view]);

  // --- Handlers ---
  const triggerPop = (e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    const id = Date.now();
    setPopHearts((prev) => [...prev, { id, x: e.clientX, y: e.clientY }]);
    setTimeout(() => setPopHearts((prev) => prev.filter(h => h.id !== id)), 1000);
  };

  const scrollToSection = (sectionId) => {
    setIsMenuOpen(false);
    if (view !== 'landing') {
      setView('landing');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleWhatsAppChat = () => {
    const phoneNumber = "916026508672";
    let msg = lang === 'en' ? "Hi! I want to plan an event in Chennai." : "வணக்கம்! நான் ஒரு நிகழ்வை திட்டமிட விரும்புகிறேன்.";
    window.open(`https://wa.me/${phoneNumber}?text=${msg}`, '_blank');
  };

  // --- Header Component ---
  const Header = () => {
    const navIds = ['services-section', 'gallery-section', 'reviews-section', 'contact-section', 'track'];

    return (
      <header className={`fixed w-full top-0 z-[100] border-b transition-all duration-300 ${darkMode ? 'bg-slate-950/90 border-slate-800 text-white' : 'bg-white/95 border-slate-200 shadow-md text-slate-900'} backdrop-blur-md`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => { setView('landing'); setIsMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden ${darkMode ? 'bg-amber-400' : 'bg-amber-950 shadow-lg'}`}>
              <img src={logo} alt="logo" className='w-full h-full object-cover' />
            </div>
            <div className="flex flex-col uppercase">
              <span className="font-black text-lg leading-none">Best</span>
              <span className="font-bold text-[10px] tracking-[0.3em] text-amber-600">Surprise</span>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-8">
            {t.nav.map((item, idx) => (
              <button
                key={item}
                onClick={() => {
                  if (navIds[idx] === 'track') navigate('/search-booking');
                  else scrollToSection(navIds[idx]);
                }}
                className={`text-[11px] font-black uppercase tracking-[0.2em] hover:text-amber-500 transition-colors ${navIds[idx] === 'track' ? 'text-amber-500 flex items-center gap-2' : ''}`}
              >
                {navIds[idx] === 'track' && <Search size={12} />}
                {item}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button onClick={() => setDarkMode(!darkMode)} className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 transition-all">
              {darkMode ? <Sun size={18} className="text-amber-400" /> : <Moon size={18} />}
            </button>
            <button onClick={() => setLang(lang === 'en' ? 'ta' : 'en')} className="hidden sm:block text-[10px] font-bold border px-3 py-1.5 rounded-xl uppercase">{lang === 'en' ? 'தமிழ்' : 'EN'}</button>
            <button onClick={() => { setView('booking'); setBookingStep(1); }} className="hidden md:block bg-[#0047AB] text-white px-5 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-blue-700 transition-all">{t.ctaBook}</button>
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden p-2 text-slate-500">
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        <div className={`fixed inset-0 top-20 z-[90] lg:hidden transition-all duration-500 transform ${isMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
          <div className={`w-full h-screen p-8 ${darkMode ? 'bg-slate-950 text-white' : 'bg-white text-slate-900'}`}>
            <ul className="space-y-6">
              {t.nav.map((item, idx) => (
                <li key={item}>
                  <button
                    onClick={() => {
                      if (navIds[idx] === 'track') navigate('/app/best-surprise/your-booking');
                      else scrollToSection(navIds[idx]);
                    }}
                    className="w-full text-3xl font-black uppercase tracking-tighter flex justify-between items-center"
                  >
                    <span className="flex items-center gap-3">
                      {navIds[idx] === 'track' && <Search size={24} className="text-amber-500" />}
                      {item}
                    </span>
                    <ChevronRight className="text-amber-500" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-12 space-y-4">
              <button onClick={() => { setView('booking'); setIsMenuOpen(false); }} className="w-full bg-amber-400 text-amber-950 py-5 rounded-2xl font-black uppercase tracking-widest">{t.ctaBook}</button>
              <button onClick={() => { setLang(lang === 'en' ? 'ta' : 'en'); setIsMenuOpen(false); }} className="w-full border-2 border-slate-200 dark:border-slate-800 py-4 rounded-2xl font-bold uppercase tracking-widest">{lang === 'en' ? 'தமிழ் மொழி' : 'English Language'}</button>
            </div>
          </div>
        </div>
      </header>
    );
  };

  const LandingView = () => (
    <>
      {/* Hero Section */}
      <section className="relative min-h-[95vh] flex items-center justify-center pt-20 overflow-hidden">
        <div className="absolute inset-0">
          {heroImages.map((img, idx) => (
            <div key={idx} className={`absolute inset-0 transition-all duration-1000 ${activeSlide === idx ? 'opacity-100 scale-105' : 'opacity-0 scale-100'}`}>
              <img src={img} alt="Celebration" className="w-full h-full object-cover" />
            </div>
          ))}
          <div className={`absolute inset-0 bg-gradient-to-b ${darkMode ? 'from-slate-950/80 via-slate-950/40 to-slate-950' : 'from-black/60 via-black/20 to-white'}`}></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl">
          <span className="inline-block px-4 py-1.5 mb-6 bg-amber-400/20 border border-amber-400/30 rounded-full text-amber-400 text-[10px] font-bold uppercase tracking-widest">{t.heroTag}</span>
          <h1 className="text-5xl md:text-8xl font-black mb-6 text-white leading-tight drop-shadow-2xl">{t.heroTitle}</h1>
          <p className="text-white/90 max-w-xl mx-auto mb-12 text-sm md:text-lg">{t.heroSub}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { setView('booking'); setBookingStep(1); }} className="bg-amber-400 text-amber-950 px-12 py-5 rounded-full font-black uppercase text-xs tracking-widest shadow-2xl hover:scale-105 transition-all">{t.ctaBook}</button>
            <button onClick={() => handleWhatsAppChat()} className="bg-white/10 backdrop-blur-md border border-white/30 text-white px-10 py-5 rounded-full font-black uppercase text-xs tracking-widest hover:bg-white/20 transition-all">{t.ctaChat}</button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services-section" className={`py-24 px-4 ${darkMode ? 'bg-slate-900/50' : 'bg-slate-50'}`}>
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-16">Services We Offer</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.services.map((service, i) => (
              <div key={i} className={`group rounded-[2.5rem] overflow-hidden border transition-all duration-500 ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100 shadow-xl'}`}>
                <div className="h-56 overflow-hidden relative">
                  <img src={service.img} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={service.title} />
                  <div className="absolute top-6 left-6 bg-white/90 dark:bg-slate-800/90 p-3 rounded-2xl backdrop-blur-sm shadow-md">{service.icon}</div>
                </div>
                <div className="p-8 text-left">
                  <h3 className="text-2xl font-black mb-4">{service.title}</h3>
                  <p className="text-sm leading-relaxed mb-8 opacity-70 font-light">{service.desc}</p>
                  <button onClick={() => setView('booking')} className="text-xs font-black text-amber-500 uppercase tracking-widest flex items-center gap-2">Explore Package <ChevronRight size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery-section" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <h2 className="text-5xl md:text-7xl font-black tracking-tighter">Memory <span className="text-amber-500 italic">Wall</span></h2>
              <p className="opacity-50 mt-4 max-w-sm text-[11px] font-bold uppercase tracking-[0.25em]">Capturing raw emotions across Chennai.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[220px] md:auto-rows-[280px]">
            {galleryImages.map((img, idx) => (
              <div key={idx} onClick={() => setSelectedImg(img)} className={`${img.size} group relative overflow-hidden rounded-[2.5rem] border cursor-zoom-in transition-all duration-700 hover:shadow-2xl ${darkMode ? 'border-white/5 bg-slate-900' : 'border-slate-100 bg-white'}`}>
                <img src={img.url} alt={img.title} className="w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-8 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                  <span className="text-[10px] font-black uppercase tracking-widest text-amber-400">{img.tag}</span>
                  <h4 className="text-white font-bold text-xl md:text-2xl">{img.title}</h4>
                </div>
                <div className="absolute top-6 right-6 p-3 bg-white/10 backdrop-blur-md rounded-full opacity-0 group-hover:opacity-100 transition-all"><Maximize2 size={18} className="text-white" /></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews-section" className={`py-32 overflow-hidden relative ${darkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <div className="max-w-7xl mx-auto px-4 mb-20 relative z-10 text-center">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-8">
            <img src={googleIconUrl} className="w-5 h-5" alt="Google" />
            <span className="text-xs font-black uppercase tracking-widest text-amber-600">Trustpilot & Google Verified</span>
          </div>
          <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-6">Loved by <span className="text-amber-500">Thousands.</span></h2>
          <div className="flex items-center justify-center gap-4">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map(i => <img key={i} src={`https://i.pravatar.cc/100?u=${i + 20}`} className="w-10 h-10 rounded-full border-2 border-white dark:border-slate-900 shadow-lg" alt="user" />)}
            </div>
            <div className="h-10 w-[1px] bg-slate-200 dark:bg-slate-800 mx-2"></div>
            <div className="text-left">
              <div className="flex gap-0.5"><Star size={14} className="text-[#FBBC05] fill-[#FBBC05]" /><Star size={14} className="text-[#FBBC05] fill-[#FBBC05]" /><Star size={14} className="text-[#FBBC05] fill-[#FBBC05]" /><Star size={14} className="text-[#FBBC05] fill-[#FBBC05]" /><Star size={14} className="text-[#FBBC05] fill-[#FBBC05]" /></div>
              <p className="text-[10px] font-black uppercase tracking-tighter opacity-50">4.9/5 Rating from 267+ reviews</p>
            </div>
          </div>
        </div>

        <div className="relative flex overflow-hidden py-10">
          <div className="reviews-track gap-6 px-8">
            {[...allReviews, ...allReviews].map((review, i) => (
              <div
                key={i}
                className={`w-[380px] shrink-0 p-6 rounded-xl border transition-all duration-300 ${darkMode
                  ? 'bg-[#202124] border-gray-700 text-white'
                  : 'bg-white border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.08)] text-gray-900'
                  }`}
              >
                {/* Header: Google Profile Circle and Info */}
                <div className="flex items-center gap-3 mb-3">
                  {/* Default Google Blue Avatar */}
                  <div className="w-10 h-10 rounded-full bg-[#1a73e8] flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </div>

                  <div className="flex flex-col">
                    <h4 className="font-medium text-[14px] flex items-center gap-1.5">
                      {review.name}
                      <CheckCircle2 size={14} className="text-blue-500 fill-blue-500/10" />
                    </h4>
                    <div className="flex items-center gap-1">
                      <span className="text-[12px] text-gray-500">{review.time}</span>
                    </div>
                  </div>
                </div>

                {/* Stars - Using Google's Official Gold */}
                <div className="flex gap-0.5 mb-2">
                  {[...Array(5)].map((_, j) => (
                    <svg key={j} className="w-4 h-4 text-[#FABB05] fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.97a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.388 2.46a1 1 0 00-.364 1.118l1.286 3.97c.3.921-.755 1.688-1.54 1.118l-3.388-2.46a1 1 0 00-1.175 0l-3.388 2.46c-.784.57-1.838-.197-1.539-1.118l1.285-3.97a1 1 0 00-.364-1.118L2.245 9.397c-.783-.57-.38-1.81.588-1.81h4.181a1 1 0 00.951-.69l1.286-3.97z" />
                    </svg>
                  ))}
                </div>

                {/* Review Content */}
                <p className={`text-[14px] leading-relaxed line-clamp-5 ${darkMode ? 'text-gray-300' : 'text-[#3c4043]'}`}>
                  {review.text}
                </p>

                {/* Google Branding Badge */}
                <div className="mt-4 pt-4 border-t border-gray-50 dark:border-gray-800/50 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" className="w-4 h-4">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    <span className="text-[11px] font-medium text-gray-400">Google Review</span>
                  </div>
                  <span className="text-[10px] text-blue-500 font-semibold cursor-pointer hover:underline">Read More</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );


  return (
    <div className={`min-h-screen transition-colors duration-500 ${darkMode ? 'bg-slate-950 text-white' : 'bg-white text-slate-900'} overflow-x-hidden relative`}>
      <style>{`
        @keyframes floatHeart { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(-100px) scale(1.6); opacity: 0; } }
        .animate-heart { position: fixed; pointer-events: none; z-index: 1000; animation: floatHeart 1s forwards; color: #f59e0b; }
        @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(calc(-400px * 6 - 2rem * 6)); } }
        .reviews-track { display: flex; width: max-content; animation: scroll 50s linear infinite; }
        .reviews-track:hover { animation-play-state: paused; }
      `}</style>

      {popHearts.map(h => <Heart key={h.id} size={28} fill="currentColor" className="animate-heart" style={{ left: h.x - 14, top: h.y - 14 }} />)}

      <Header />
      <main onClick={triggerPop}>
        {view === "landing" ? (
          <LandingView />
        ) : (
          <BookingView
            darkMode={darkMode}
            bookingStep={bookingStep}
            setBookingStep={setBookingStep}
            setView={setView}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            userData={userData}
            setUserData={setUserData}
            handleWhatsAppChat={handleWhatsAppChat}
          />
        )}
      </main>


      {view === 'landing' && (
        <footer
          id="contact-section"
          className={`pt-12 pb-6 md:pt-20 md:pb-10 px-6 transition-colors duration-500 ${darkMode ? 'bg-slate-950' : 'bg-[#f59e0b]'
            } text-white`}
        >
          <div className="max-w-7xl mx-auto">
            {/* Reduced gap for mobile, kept original for desktop */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 mb-10 md:mb-16">

              {/* Brand Section: Centered on mobile for better flow */}
              <div className="space-y-4 md:space-y-6 text-center md:text-left">
                <div
                  className="flex items-center justify-center md:justify-start gap-2 cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <Star className={`${darkMode ? 'text-amber-400 fill-amber-400' : 'text-white fill-white'}`} size={20} />
                  <span className="text-xl md:text-2xl font-black uppercase tracking-tighter">best surprise</span>
                </div>
                <p className="text-xs md:text-sm opacity-90 font-medium max-w-xs mx-auto md:mx-0">
                  Chennai's #1 boutique surprise planning agency.
                </p>
              </div>

              {/* Navigation: Two columns on mobile to save vertical space */}
              <div className="border-t border-white/10 pt-6 md:border-0 md:pt-0">
                <h4 className={`font-bold uppercase tracking-[0.2em] text-[10px] mb-4 md:mb-8 ${darkMode ? 'text-amber-400' : 'text-amber-950'}`}>
                  Navigation
                </h4>
                <ul className="grid grid-cols-2 md:grid-cols-1 gap-3 text-sm font-medium">
                  <li className="cursor-pointer hover:opacity-70 transition-opacity" onClick={() => scrollToSection('services-section')}>Services</li>
                  <li className="cursor-pointer hover:opacity-70 transition-opacity" onClick={() => scrollToSection('gallery-section')}>Gallery</li>
                  <li className="cursor-pointer hover:opacity-70 transition-opacity" onClick={() => scrollToSection('reviews-section')}>Reviews</li>
                  <li className="cursor-pointer flex items-center gap-2 underline underline-offset-4" onClick={() => navigate('/app/best-surprise/your-booking')}>
                    <Search size={14} /> Track
                  </li>
                </ul>
              </div>

              {/* Locations */}
              <div>
                <h4 className={`font-bold uppercase tracking-[0.2em] text-[10px] mb-4 md:mb-8 ${darkMode ? 'text-amber-400' : 'text-amber-950'}`}>
                  Location
                </h4>
                <p className="text-xs md:text-sm opacity-90 leading-relaxed">
                  2nd Floor, 53, Madhavaram High Rd, Perambur, Chennai 600011
                </p>
              </div>

              {/* Contact */}
              <div>
                <h4 className={`font-bold uppercase tracking-[0.2em] text-[10px] mb-4 md:mb-8 ${darkMode ? 'text-amber-400' : 'text-amber-950'}`}>
                  Contact
                </h4>
                <div className="space-y-1 md:space-y-2 font-bold text-sm">
                  <p>+91 6026508672</p>
                  <p className="opacity-90">info@bestsurprise.in</p>
                </div>
              </div>
            </div>

            <div className={`pt-6 border-t ${darkMode ? 'border-slate-800' : 'border-amber-600/30'}`}>
              <p className="text-center text-[9px] font-bold opacity-60 tracking-[0.2em] md:tracking-[0.4em]">
                © 2026 best surprise CHENNAI.
              </p>
            </div>
          </div>
        </footer>
      )}

      {view === 'landing' && <button onClick={() => handleWhatsAppChat()} className="fixed bottom-8 right-8 bg-[#25D366] text-white p-5 rounded-full shadow-2xl z-[150] hover:scale-110 transition-transform"><MessageCircle size={32} fill="white" /></button>}

      {selectedImg && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/95 backdrop-blur-xl" onClick={() => setSelectedImg(null)}>
          <button className="absolute top-10 right-10 text-white hover:text-amber-400 transition-colors"><X size={40} /></button>
          <img src={selectedImg.url} className="max-w-full max-h-[80vh] rounded-3xl shadow-2xl border-4 border-white/10" alt="Large" />
        </div>
      )}
    </div>
  );
};

export default BestSurpriseApp;